import type { PathCommand } from '@/lib/elements/types/path-command'

/**
 * Compute the bounding box of all points in path commands (moveTo, lineTo, curveTo).
 */
export function pathCommandsBBox(
  commands: PathCommand[]
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity

  const add = (x: number, y: number) => {
    if (Number.isFinite(x)) {
      minX = Math.min(minX, x)
      maxX = Math.max(maxX, x)
    }

    if (Number.isFinite(y)) {
      minY = Math.min(minY, y)
      maxY = Math.max(maxY, y)
    }
  }

  for (const cmd of commands) {
    if ('moveTo' in cmd) {
      add(cmd.moveTo.x, cmd.moveTo.y)
    } else if ('lineTo' in cmd) {
      add(cmd.lineTo.x, cmd.lineTo.y)
    } else if ('curveTo' in cmd) {
      const ct = cmd.curveTo

      add(ct.controlX, ct.controlY)
      add(ct.controlX2 ?? ct.deltaX ?? ct.controlX, ct.controlY2 ?? ct.deltaY ?? ct.controlY)
      add(ct.x, ct.y)
    }
  }

  if (minX === Infinity || minY === Infinity) return null

  return { minX, minY, maxX, maxY }
}

export const BBOX_TOLERANCE = 0.02

/**
 * Prepare path commands for SVG: editor paths are already in element space (0..width × 0..height).
 * Only apply {@link scalePathCommandsToFit} for legacy normalized paths (~0..1), e.g. older API payloads.
 *
 * Using scalePathCommandsToFit on every render breaks cubics whose curve extends outside the control-point
 * bounding box (e.g. 4-segment circles): the bbox underestimates extent → wrong scale/offset → warped paths.
 */
export function preparePathCommandsForSvgRender(
  commands: PathCommand[],
  targetWidth: number,
  targetHeight: number
): PathCommand[] {
  if (commands.length === 0 || targetWidth <= 0 || targetHeight <= 0) {
    return commands
  }

  const bbox = pathCommandsBBox(commands)

  if (!bbox) {
    return commands
  }

  const spanX = bbox.maxX - bbox.minX
  const spanY = bbox.maxY - bbox.minY

  const looksLikeNormalizedLegacy =
    spanX > 0 &&
    spanY > 0 &&
    bbox.minX >= -1e-6 &&
    bbox.minY >= -1e-6 &&
    bbox.maxX <= 1.0001 &&
    bbox.maxY <= 1.0001 &&
    (targetWidth > 2 || targetHeight > 2)

  if (looksLikeNormalizedLegacy) {
    return scalePathCommandsToFit(commands, targetWidth, targetHeight)
  }

  return commands
}

/**
 * Scale path commands to the shape's dimensions. If the path is already in (0,0)-(targetWidth, targetHeight)
 * we leave it unchanged to avoid distortion. Otherwise we scale uniformly (preserve aspect ratio) and center
 * so the heart doesn't get stretched when the API returns a different coordinate system.
 */
export function scalePathCommandsToFit(
  commands: PathCommand[],
  targetWidth: number,
  targetHeight: number
): PathCommand[] {
  const bbox = pathCommandsBBox(commands)

  if (!bbox || commands.length === 0) return commands

  const spanX = bbox.maxX - bbox.minX || 1
  const spanY = bbox.maxY - bbox.minY || 1

  const alreadyFits =
    Math.abs(bbox.minX) <= BBOX_TOLERANCE * targetWidth &&
    Math.abs(bbox.minY) <= BBOX_TOLERANCE * targetHeight &&
    Math.abs(bbox.maxX - targetWidth) <= BBOX_TOLERANCE * targetWidth &&
    Math.abs(bbox.maxY - targetHeight) <= BBOX_TOLERANCE * targetHeight

  if (alreadyFits) return commands

  const scaleX = targetWidth / spanX
  const scaleY = targetHeight / spanY
  const scale = Math.min(scaleX, scaleY)
  const scaledSpanX = spanX * scale
  const scaledSpanY = spanY * scale
  const offsetX = (targetWidth - scaledSpanX) / 2 - bbox.minX * scale
  const offsetY = (targetHeight - scaledSpanY) / 2 - bbox.minY * scale

  const transform = (x: number, y: number) => ({
    x: x * scale + offsetX,
    y: y * scale + offsetY,
  })

  return commands.map((cmd) => {
    if ('moveTo' in cmd) {
      const p = transform(cmd.moveTo.x, cmd.moveTo.y)

      return { moveTo: { x: p.x, y: p.y } }
    }

    if ('lineTo' in cmd) {
      const p = transform(cmd.lineTo.x, cmd.lineTo.y)

      return { lineTo: { x: p.x, y: p.y } }
    }

    if ('curveTo' in cmd) {
      const ct = cmd.curveTo
      const c1 = transform(ct.controlX, ct.controlY)
      const c2 = transform(
        ct.controlX2 ?? ct.deltaX ?? ct.controlX,
        ct.controlY2 ?? ct.deltaY ?? ct.controlY
      )
      const end = transform(ct.x, ct.y)

      return {
        curveTo: {
          controlX: c1.x,
          controlY: c1.y,
          controlX2: c2.x,
          controlY2: c2.y,
          x: end.x,
          y: end.y,
        },
      }
    }

    return cmd
  })
}
